import { prop } from 'ramda';
import { useMemo, useCallback } from 'react';
import { useUserSelector } from '@core/js/store/hooks';

import { CURRENCIES } from '@core/js/hooks/useFormatter/constants';
import { useGetConfig } from '@core/js/hooks/useFormatter/hooks/useGetConfig';
import { useNumberFormatter } from '@core/js/hooks/useFormatter/hooks/useNumberFormatter';

export const useCurrencyFormatter = () => {
    const getConfig = useGetConfig();
    const { formatNumber } = useNumberFormatter();
    const userCurrencyCode = useUserSelector(prop('currencyCode'));

    const getCurrencySymbol = useCallback((options) => {
        const currencyCode = typeof options === 'object'
            ? options.currencyCode || userCurrencyCode
            : options || userCurrencyCode;

        return CURRENCIES[currencyCode?.toUpperCase()] || currencyCode?.toUpperCase() || '';
    }, [userCurrencyCode]);

    const formatCurrency = useCallback((amount, options = {}, locale) => {
        const { currencyCode = userCurrencyCode } = options;
        const opts = {
            ...getConfig(options.locale || locale).currency,
            ...options
        };

        const formattedCurrency = [
            getCurrencySymbol({ currencyCode }),
            formatNumber(amount, opts, locale)
        ];

        return (
            opts.symbolAtTheLeft
                ? formattedCurrency
                : formattedCurrency.reverse()
        ).join('').trim(); // @theme
    }, [getConfig, formatNumber, getCurrencySymbol, userCurrencyCode]);

    const getIsCurrencySymbolAtTheLeft = useCallback((options = {}) => {
        const opts = {
            ...getConfig(options.locale).currency,
            ...options
        };
        const { symbolAtTheLeft = true } = opts;

        return symbolAtTheLeft;
    }, [getConfig]);

    // #region @theme
    const getRoundingBalance = useCallback((amount, options = {}) => {
        const { currencyCode = userCurrencyCode } = options;
        const roundedAmount = Math.floor(amount).toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        const formattedCurrency = [
            getCurrencySymbol({ currencyCode }),
            roundedAmount
        ];

        return formattedCurrency.join('').trim();
    }, [getCurrencySymbol, userCurrencyCode]);

    const formatKCash = useCallback(n => {
        return n < 1e3 ? n : `${Number((n / 1e3).toFixed(1))}K`;
    }, []);
    // #endregion

    return useMemo(() => ({
        formatCurrency,
        getCurrencySymbol,
        getIsCurrencySymbolAtTheLeft,
        // #region @theme
        getRoundingBalance,
        formatKCash
        // #endregion
    }), [formatCurrency, getCurrencySymbol, getIsCurrencySymbolAtTheLeft, getRoundingBalance, formatKCash]);
};
